import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/app/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Seo } from "@/components/site/Seo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    const result = await register({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      role: "individual",
    });
    setLoading(false);
    if (result.ok) navigate("/app/discover", { replace: true });
    else setError(result.error);
  };

  return (
    <div className="min-h-screen bg-surface px-5 py-10 sm:flex sm:items-center sm:justify-center">
      <Seo noindex title="Start your journey — OpenDegree" description="Create an OpenDegree learner account." path="/app/register" />
      <div className="mx-auto w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2.5">
          <Logo markClass="h-11 w-11" textClass="text-2xl" />
        </Link>
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgba(10,37,64,0.06)]">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-cobalt">Learner journey</p>
          <h1 className="mt-2 font-display text-2xl font-bold tracking-tight text-ink">Create your account</h1>
          <p className="mt-1 text-sm leading-6 text-slatey">Create a learner account to save your journey, continue assessments and apply. Your profile builds progressively as information becomes relevant.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <div>
              <label className="text-sm font-medium text-ink">Full name</label>
              <input data-testid="register-name" required value={form.name} onChange={(event) => update("name", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20" placeholder="Your name" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Email</label>
              <input data-testid="register-email" type="email" required value={form.email} onChange={(event) => update("email", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Password</label>
              <input data-testid="register-password" type="password" required minLength={8} value={form.password} onChange={(event) => update("password", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20" placeholder="At least 8 characters" />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Confirm password</label>
              <input data-testid="register-confirm-password" type="password" required minLength={8} value={form.confirmPassword} onChange={(event) => update("confirmPassword", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-[15px] outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/20" placeholder="Repeat your password" />
            </div>
            {error && <p data-testid="register-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p>}
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cobalt px-6 py-3.5 text-[15px] font-semibold text-white transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</> : <>Start your journey <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-slatey">
            Already have an account? <Link to="/app/login" data-testid="register-login-link" className="font-bold text-cobalt">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
